import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Alert,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  SmartToy,
  Home,
  AdminPanelSettings,
  Person,
  Message,
  AccessTime,
  Add,
  Edit,
  Delete,
  Memory,
  Chat,
  Refresh,
  CheckCircle,
  Cancel,
  LightbulbOutlined as ImproveIcon,
  ExpandMore,
  Build as ToolIcon,
  FiberManualRecord as ActiveDot,
  ContentCopy,
  ErrorOutline,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useApi';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { ImproveMessageModal } from '../../components/ImproveMessageModal';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface ChatSession {
  sessionId: string;
  userId: string;
  userEmail: string;
  userName: string;
  title: string;
  lastActivity: string;
  messageCount: number;
  firstMessageTime: string;
  hasErrors: boolean;
  estimatedCost: number;
}

interface KnowledgeEntry {
  id: number;
  category: string;
  topic: string;
  content: string;
  priority: number;
  is_active: boolean;
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string | null;
  creator_email?: string;
  updater_email?: string;
}

interface UsageData {
  dates: string[];
  users: { [email: string]: number[] };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`mia-oversight-tabpanel-${index}`}
      aria-labelledby={`mia-oversight-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CATEGORIES = [
  { value: 'market_terminology', label: 'Market Terminology' },
  { value: 'data_interpretation', label: 'Data Interpretation' },
  { value: 'tool_usage', label: 'Tool Usage' },
  { value: 'corrections', label: 'Corrections' },
  { value: 'general_advice', label: 'General Advice' },
  { value: 'best_practices', label: 'Best Practices' },
  { value: 'error_handling', label: 'Error Handling' },
];

export const MiaOversightPage: React.FC = () => {
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Chat sessions state (no longer using modal)

  // Improve Mia modal state
  const [improveModalOpen, setImproveModalOpen] = useState(false);
  const [improveMessageId, setImproveMessageId] = useState<string>('');
  const [improveMessageContent, setImproveMessageContent] = useState<string>('');

  // Knowledge management state
  const [knowledgeFilter, setKnowledgeFilter] = useState<string>('all');
  const [activeFilter, setActiveFilter] = useState<string>('true');
  const [knowledgeDialogOpen, setKnowledgeDialogOpen] = useState(false);
  const [editingKnowledge, setEditingKnowledge] = useState<KnowledgeEntry | null>(null);
  const [knowledgeForm, setKnowledgeForm] = useState({
    category: '',
    topic: '',
    content: '',
    priority: 5,
  });

  // Fetch recent chat sessions
  // Auto-refresh every 10 seconds
  const { data: sessionsData, isLoading: isLoadingSessions, refetch: refetchSessions } = useQuery({
    queryKey: ['mia-oversight-sessions'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/mia-oversight/admin/sessions`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch chat sessions');
      }
      return await response.json();
    },
    enabled: !!user?.permissions?.includes('market_compass.admin'),
    refetchInterval: 10000, // Poll every 10 seconds
    refetchIntervalInBackground: false, // Stop polling when tab is not visible
  });

  // Fetch knowledge entries
  const { data: knowledgeData, isLoading: isLoadingKnowledge, refetch: refetchKnowledge } = useQuery({
    queryKey: ['mia-knowledge', knowledgeFilter, activeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (knowledgeFilter !== 'all') params.append('category', knowledgeFilter);
      if (activeFilter !== 'all') params.append('isActive', activeFilter);

      const response = await authenticatedFetch(
        `${API_URL}/api/mobile/admin/knowledge?${params.toString()}`
      );
      if (!response || !response.ok) {
        throw new Error('Failed to fetch knowledge entries');
      }
      return await response.json();
    },
    enabled: !!user?.permissions?.includes('market_compass.admin'),
  });

  // Fetch usage statistics
  const { data: usageData, isLoading: isLoadingUsage, refetch: refetchUsage } = useQuery({
    queryKey: ['mia-usage'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/mia-oversight/admin/usage`);
      if (!response || !response.ok) {
        throw new Error('Failed to fetch usage statistics');
      }
      return await response.json();
    },
    enabled: !!user?.permissions?.includes('market_compass.admin'),
  });

  // Create knowledge entry mutation
  const createKnowledgeMutation = useMutation({
    mutationFn: async (data: typeof knowledgeForm) => {
      const response = await authenticatedFetch(`${API_URL}/api/mobile/admin/knowledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response || !response.ok) {
        throw new Error('Failed to create knowledge entry');
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mia-knowledge'] });
      setKnowledgeDialogOpen(false);
      resetKnowledgeForm();
    },
  });

  // Update knowledge entry mutation
  const updateKnowledgeMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: typeof knowledgeForm }) => {
      const response = await authenticatedFetch(`${API_URL}/api/mobile/admin/knowledge/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!response || !response.ok) {
        throw new Error('Failed to update knowledge entry');
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mia-knowledge'] });
      setKnowledgeDialogOpen(false);
      setEditingKnowledge(null);
      resetKnowledgeForm();
    },
  });

  // Delete knowledge entry mutation
  const deleteKnowledgeMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await authenticatedFetch(`${API_URL}/api/mobile/admin/knowledge/${id}`, {
        method: 'DELETE',
      });
      if (!response || !response.ok) {
        throw new Error('Failed to delete knowledge entry');
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mia-knowledge'] });
    },
  });

  // Show loading spinner while user data is being fetched
  if (isLoadingUser) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have market_compass.admin permission (after loading completes)
  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  const sessions: ChatSession[] = sessionsData?.data || [];
  const knowledgeEntries: KnowledgeEntry[] = knowledgeData?.data?.entries || [];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };

  const isSessionActive = (session: ChatSession, allSessions: ChatSession[]) => {
    const lastActivity = new Date(session.lastActivity);
    const now = new Date();
    const diffMs = now.getTime() - lastActivity.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    // Only consider sessions within 30 minutes
    if (diffMins > 30) return false;

    // Check if this is the most recent session for this user
    const userSessions = allSessions.filter(s => s.userId === session.userId);
    const mostRecentSession = userSessions.reduce((latest, current) => {
      return new Date(current.lastActivity) > new Date(latest.lastActivity) ? current : latest;
    }, userSessions[0]);

    return mostRecentSession.sessionId === session.sessionId;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const resetKnowledgeForm = () => {
    setKnowledgeForm({
      category: '',
      topic: '',
      content: '',
      priority: 5,
    });
  };

  const handleCreateKnowledge = () => {
    resetKnowledgeForm();
    setEditingKnowledge(null);
    setKnowledgeDialogOpen(true);
  };

  const handleEditKnowledge = (entry: KnowledgeEntry) => {
    setEditingKnowledge(entry);
    setKnowledgeForm({
      category: entry.category,
      topic: entry.topic,
      content: entry.content,
      priority: entry.priority,
    });
    setKnowledgeDialogOpen(true);
  };

  const handleSaveKnowledge = () => {
    if (editingKnowledge) {
      updateKnowledgeMutation.mutate({
        id: editingKnowledge.id,
        data: knowledgeForm,
      });
    } else {
      createKnowledgeMutation.mutate(knowledgeForm);
    }
  };

  const handleDeleteKnowledge = (id: number) => {
    if (window.confirm('Are you sure you want to deactivate this knowledge entry?')) {
      deleteKnowledgeMutation.mutate(id);
    }
  };

  const getCategoryLabel = (category: string) => {
    return CATEGORIES.find((c) => c.value === category)?.label || category;
  };

  const handleImproveMessage = (messageId: string, messageContent: string) => {
    setImproveMessageId(messageId);
    setImproveMessageContent(messageContent);
    setImproveModalOpen(true);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <SmartToy sx={{ mr: 0.5 }} fontSize="inherit" />
            Mia Oversight
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <SmartToy sx={{ fontSize: 48, color: '#F5A800' }} />
            <Typography variant="h4" component="h1" sx={{ color: '#607D8B' }}>
              Mia Oversight
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Monitor and manage Mia chat interactions and knowledge base
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper elevation={2}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: '#F8F9FA'
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              TabIndicatorProps={{
                style: { backgroundColor: '#F5A800', height: 3 }
              }}
              sx={{
                '& .MuiTab-root': {
                  color: '#607D8B',
                  '&.Mui-selected': {
                    color: '#003F9F',
                    fontWeight: 600,
                  }
                }
              }}
            >
              <Tab icon={<Chat />} label="Chat Sessions" iconPosition="start" />
              <Tab icon={<Memory />} label="Mia Memories" iconPosition="start" />
              <Tab icon={<BarChartIcon />} label="Usage" iconPosition="start" />
            </Tabs>
          </Box>

          {/* Chat Sessions Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#607D8B' }}>
                Recent Chat Sessions (Last 5 Days)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Chip
                  label={`${sessions.filter(s => isSessionActive(s, sessions)).length} Active (30 min)`}
                  icon={<ActiveDot sx={{ fontSize: 16 }} />}
                  sx={{
                    bgcolor: '#4CAF50',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    '& .MuiChip-icon': {
                      color: '#FFFFFF'
                    }
                  }}
                />
                <Chip
                  label={`${sessions.length} Total`}
                  sx={{
                    bgcolor: '#607D8B',
                    color: '#FFFFFF',
                    fontWeight: 600
                  }}
                />
                <Chip
                  label={`$${sessions.reduce((sum, s) => sum + s.estimatedCost, 0).toFixed(2)}`}
                  sx={{
                    bgcolor: '#F5A800',
                    color: '#FFFFFF',
                    fontWeight: 600
                  }}
                />
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={() => refetchSessions()}
                    sx={{
                      color: '#607D8B',
                      '&:hover': {
                        bgcolor: '#F8F9FA'
                      }
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {isLoadingSessions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : sessions.length === 0 ? (
              <Alert severity="info">
                No chat sessions found in the last 5 days.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {sessions.map((session) => {
                  const isActive = isSessionActive(session, sessions);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={session.sessionId}>
                      <Card
                        variant="outlined"
                        sx={{
                          borderColor: isActive ? '#4CAF50' : undefined,
                          borderWidth: isActive ? 2 : 1,
                          position: 'relative',
                        }}
                      >
                        {isActive && (
                          <Chip
                            label="Active"
                            icon={<ActiveDot sx={{ fontSize: 12 }} />}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              bgcolor: '#4CAF50',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 24,
                              zIndex: 1,
                              '& .MuiChip-icon': {
                                color: '#FFFFFF',
                                fontSize: 12,
                              }
                            }}
                          />
                        )}
                        <CardActionArea onClick={() => navigate(`/admin/mia-oversight/session/${session.sessionId}`)}>
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Typography variant="subtitle1" noWrap sx={{ pr: isActive ? 8 : 0, flex: 1 }}>
                                {session.title}
                              </Typography>
                              {session.hasErrors && (
                                <Tooltip title="Session contains errors">
                                  <ErrorOutline sx={{ fontSize: 20, color: '#d32f2f' }} />
                                </Tooltip>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {session.userEmail}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <Message sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {session.messageCount} messages
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2" color="text.secondary">
                                {formatRelativeTime(session.lastActivity)}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Memory sx={{ fontSize: 16, color: '#F5A800' }} />
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#F5A800' }}>
                                ${session.estimatedCost.toFixed(6)}
                              </Typography>
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </TabPanel>

          {/* Mia Memories Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={knowledgeFilter}
                    label="Category"
                    onChange={(e: SelectChangeEvent) => setKnowledgeFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {CATEGORIES.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={activeFilter}
                    label="Status"
                    onChange={(e: SelectChangeEvent) => setActiveFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="true">Active</MenuItem>
                    <MenuItem value="false">Inactive</MenuItem>
                  </Select>
                </FormControl>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={() => refetchKnowledge()}
                    sx={{
                      color: '#607D8B',
                      '&:hover': {
                        bgcolor: '#F8F9FA'
                      }
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Box>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateKnowledge}
                sx={{
                  bgcolor: '#003F9F',
                  '&:hover': {
                    bgcolor: '#002A6B'
                  }
                }}
              >
                Add Memory
              </Button>
            </Box>

            {isLoadingKnowledge ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : knowledgeEntries.length === 0 ? (
              <Alert severity="info">
                No knowledge entries found matching the current filters.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell>Topic</TableCell>
                      <TableCell>Content</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {knowledgeEntries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          <Chip
                            label={getCategoryLabel(entry.category)}
                            size="small"
                            sx={{
                              bgcolor: '#607D8B',
                              color: '#FFFFFF'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#003F9F' }}>
                            {entry.topic}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography variant="body2" noWrap>
                            {entry.content}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.priority}
                            size="small"
                            sx={{
                              bgcolor: entry.priority >= 8 ? '#d32f2f' : entry.priority >= 5 ? '#F5A800' : '#686E77',
                              color: '#FFFFFF',
                              fontWeight: 600
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {entry.is_active ? (
                            <Chip icon={<CheckCircle />} label="Active" size="small" color="success" />
                          ) : (
                            <Chip icon={<Cancel />} label="Inactive" size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            {formatDate(entry.created_at)}
                          </Typography>
                          {entry.creator_email && (
                            <Typography variant="caption" color="text.secondary">
                              by {entry.creator_email}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEditKnowledge(entry)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deactivate">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteKnowledge(entry.id)}
                              disabled={!entry.is_active}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* Usage Tab */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#607D8B' }}>
                Message Usage - Past 7 Days
              </Typography>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() => refetchUsage()}
                  sx={{
                    color: '#607D8B',
                    '&:hover': {
                      bgcolor: '#F8F9FA'
                    }
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Box>

            {isLoadingUsage ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (() => {
              const usage: UsageData = usageData?.data || { dates: [], users: {} };
              const userEmails = Object.keys(usage.users);

              // Transform data for recharts
              const chartData = usage.dates.map((date, index) => {
                const dataPoint: any = {
                  date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                };
                userEmails.forEach(email => {
                  dataPoint[email] = usage.users[email][index];
                });
                return dataPoint;
              });

              // Generate distinct colors for each user
              const colors = [
                '#003F9F', '#F5A800', '#607D8B', '#4CAF50', '#d32f2f',
                '#9C27B0', '#FF9800', '#009688', '#E91E63', '#3F51B5',
                '#8BC34A', '#FF5722', '#00BCD4', '#FFEB3B', '#795548'
              ];

              return userEmails.length === 0 ? (
                <Alert severity="info">
                  No usage data found for the past 7 days.
                </Alert>
              ) : (
                <Box>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis label={{ value: 'Messages', angle: -90, position: 'insideLeft' }} />
                      <RechartsTooltip />
                      <Legend />
                      {userEmails.map((email, index) => (
                        <Bar
                          key={email}
                          dataKey={email}
                          stackId="a"
                          fill={colors[index % colors.length]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total messages in the past 7 days:{' '}
                      <strong>
                        {userEmails.reduce((sum, email) => {
                          return sum + usage.users[email].reduce((a, b) => a + b, 0);
                        }, 0)}
                      </strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active users: <strong>{userEmails.length}</strong>
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
          </TabPanel>
        </Paper>

        {/* Knowledge Entry Dialog */}
        <Dialog
          open={knowledgeDialogOpen}
          onClose={() => setKnowledgeDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: '#F8F9FA', borderBottom: '3px solid #F5A800' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Memory sx={{ color: '#F5A800', fontSize: 28 }} />
              <Typography variant="h6" sx={{ color: '#607D8B' }}>
                {editingKnowledge ? 'Edit Knowledge Entry' : 'Create Knowledge Entry'}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={knowledgeForm.category}
                  label="Category"
                  onChange={(e: SelectChangeEvent) =>
                    setKnowledgeForm({ ...knowledgeForm, category: e.target.value })
                  }
                >
                  {CATEGORIES.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                required
                label="Topic"
                value={knowledgeForm.topic}
                onChange={(e) => setKnowledgeForm({ ...knowledgeForm, topic: e.target.value })}
              />
              <TextField
                fullWidth
                required
                multiline
                rows={6}
                label="Content"
                value={knowledgeForm.content}
                onChange={(e) => setKnowledgeForm({ ...knowledgeForm, content: e.target.value })}
                helperText="Provide clear, concise guidance that Mia can use in conversations"
              />
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={knowledgeForm.priority.toString()}
                  label="Priority"
                  onChange={(e: SelectChangeEvent) =>
                    setKnowledgeForm({ ...knowledgeForm, priority: parseInt(e.target.value) })
                  }
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((p) => (
                    <MenuItem key={p} value={p}>
                      {p} {p >= 8 ? '(High)' : p >= 5 ? '(Medium)' : '(Low)'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setKnowledgeDialogOpen(false)}
              sx={{ color: '#607D8B' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveKnowledge}
              disabled={!knowledgeForm.category || !knowledgeForm.topic || !knowledgeForm.content}
              sx={{
                bgcolor: '#003F9F',
                '&:hover': {
                  bgcolor: '#002A6B'
                }
              }}
            >
              {editingKnowledge ? 'Save Changes' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Improve Mia Modal */}
        <ImproveMessageModal
          open={improveModalOpen}
          onClose={() => setImproveModalOpen(false)}
          messageId={improveMessageId}
          messageContent={improveMessageContent}
        />
      </Box>
    </Container>
  );
};
